/**
 * API error normalization.
 *
 * The backend returns a standardized shape:
 *   { "error": { "code": "forbidden", "message": "...", "details": ..., "request_id": "..." } }
 *
 * Older endpoints may return `{ "detail": "..." }` or plain text.
 * `ApiError` normalizes these into something the UI can trust.
 */

export type ApiErrorBody = {
  error?: {
    code?: string;
    message?: string;
    details?: unknown;
    request_id?: string;
  };
  detail?: string;
  message?: string;
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly requestId?: string;

  constructor(params: {
    status: number;
    code?: string;
    message: string;
    details?: unknown;
    requestId?: string;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.status = params.status;
    this.code = params.code ?? 'unknown_error';
    this.details = params.details;
    this.requestId = params.requestId;
  }

  /** True if this is an auth-related error (401/403). */
  get isAuthError(): boolean {
    return this.status === 401 || this.status === 403;
  }

  /** True if the request can reasonably be retried. */
  get isRetryable(): boolean {
    return this.status === 429 || this.status >= 500;
  }
}

/** Build an ApiError from a fetch Response, parsing the body safely. */
export async function fromResponse(res: Response): Promise<ApiError> {
  let body: ApiErrorBody | null = null;
  let rawText = '';

  try {
    rawText = await res.text();
    if (rawText) {
      body = JSON.parse(rawText) as ApiErrorBody;
    }
  } catch {
    // Non-JSON body — keep rawText
  }

  const code = body?.error?.code ?? codeFromStatus(res.status);
  const message =
    body?.error?.message ??
    body?.detail ??
    body?.message ??
    rawText ??
    `HTTP ${res.status}`;

  return new ApiError({
    status: res.status,
    code,
    message: String(message).slice(0, 500),
    details: body?.error?.details,
    requestId: body?.error?.request_id,
  });
}

function codeFromStatus(status: number): string {
  const map: Record<number, string> = {
    400: 'bad_request',
    401: 'unauthorized',
    403: 'forbidden',
    404: 'not_found',
    409: 'conflict',
    422: 'validation_error',
    423: 'locked',
    429: 'rate_limited',
    500: 'internal_error',
    502: 'bad_gateway',
    503: 'service_unavailable',
  };
  return map[status] ?? 'error';
}

/** Convenience: user-friendly message from any thrown value. */
export function parseApiError(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong.';
}
